import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';

function VatRateForm() {
  const { vatRateId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    rate: '',
    description: '',
    country_code: '',
    is_active: true,
    is_default: false
  });

  const isEditing = Boolean(vatRateId);

  useEffect(() => {
    if (vatRateId) {
      loadVatRate();
    }
  }, [vatRateId]);

  const loadVatRate = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/vat-rates/${vatRateId}`);
      const rate = response.data;
      setFormData({
        name: rate.name || '',
        code: rate.code || '',
        rate: rate.rate || '',
        description: rate.description || '',
        country_code: rate.country_code || '',
        is_active: rate.is_active !== false,
        is_default: rate.is_default || false
      });
      setError('');
    } catch (err) {
      console.error('Failed to load VAT rate:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.msg || err.message || 'Failed to load VAT rate';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      
      if (isEditing) {
        await api.put(`/vat-rates/${vatRateId}`, formData);
      } else {
        await api.post('/vat-rates', formData);
      }
      
      navigate('/vat-rates');
    } catch (err) {
      console.error('Failed to save VAT rate:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.msg || err.message || 'Failed to save VAT rate';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this VAT rate?')) {
      return;
    }

    try {
      setLoading(true);
      await api.delete(`/vat-rates/${vatRateId}`);
      navigate('/vat-rates');
    } catch (err) {
      console.error('Failed to delete VAT rate:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.msg || err.message || 'Failed to delete VAT rate';
      setError(errorMsg);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/vat-rates');
  };

  if (loading && isEditing && !formData.name) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">Dashboard</button>
        <span className="breadcrumb-separator">/</span>
        <button onClick={() => navigate('/vat-rates')} className="breadcrumb-link">VAT Rates</button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">{isEditing ? 'Edit' : 'New'}</span>
      </nav>

      {/* Main Card */}
      <div className="card">
        {/* Header with Action Buttons */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '3px solid #EF4444' }}>
          <div>
            <h2 style={{ color: 'var(--gray-900)', fontSize: '1.5rem', fontWeight: 'bold', margin: '0 0 0.25rem 0' }}>
              💰 {isEditing ? 'Edit VAT Rate' : 'Create VAT Rate'}
            </h2>
            <p style={{ color: 'var(--gray-600)', margin: 0, fontSize: '0.875rem' }}>
              {isEditing ? 'Update VAT rate information' : 'Add a new VAT rate for products'}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            {isEditing && (
              <button
                onClick={handleDelete}
                disabled={loading}
                className="btn btn-danger"
              >
                🗑️ Delete
              </button>
            )}
            <button
              onClick={handleCancel}
              disabled={loading}
              className="btn btn-outline"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="btn btn-primary"
            >
              {loading ? 'Saving...' : isEditing ? '💾 Update' : '➕ Create'}
            </button>
          </div>
        </div>

        {error && (
          <div style={{ backgroundColor: 'var(--red-50)', border: '1px solid var(--red-200)', color: 'var(--red-800)', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', cursor: 'pointer' }} onClick={() => setError('')}>
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Basic Information Section */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid var(--gray-200)' }}>
              Basic Information
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                  Code *
                </label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  required
                  placeholder="e.g., STD, RED"
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px', textTransform: 'uppercase' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                  Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Standard Rate, Reduced Rate"
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                  Rate (%) *
                </label>
                <input
                  type="number"
                  name="rate"
                  value={formData.rate}
                  onChange={handleChange}
                  required
                  step="0.01"
                  min="0"
                  max="100"
                  placeholder="e.g., 20.00"
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                  Country Code
                </label>
                <input
                  type="text"
                  name="country_code"
                  value={formData.country_code}
                  onChange={handleChange}
                  maxLength="2"
                  placeholder="e.g., GB, US, DE"
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px', textTransform: 'uppercase' }}
                />
              </div>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                placeholder="Optional description of when this VAT rate applies"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--gray-300)', borderRadius: '6px', fontFamily: 'inherit' }}
              />
            </div>
          </div>

          {/* Status Section */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid var(--gray-200)' }}>
              Status
            </h3>
            
            <div style={{ display: 'flex', gap: '2rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>Active</span>
                <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                  (Can be used for new products and orders)
                </span>
              </label>

              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="is_default"
                  checked={formData.is_default}
                  onChange={handleChange}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '0.875rem', fontWeight: '500' }}>Set as Default</span>
                <span style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                  (Automatically selected for new products)
                </span>
              </label>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default VatRateForm;
